'use client';

/**
 * "Your apps' daily compute" — the app-first face of the compute quota.
 *
 * Split out of the Spending Rules form (2026-08-11 UX review): a generic
 * user thinks in APPS and TODAY ("Reach is out of juice — give it more"),
 * not in agent usernames and standing percent fields. Money rules keep the
 * old card; this one shows a live bar per app that ran today, what its
 * share is, when it resets, and a stepped Raise menu — the same
 * `applyComputeShare` verbs the share-block notification's one-tap CTAs
 * use, so the two surfaces can never drift.
 */

import { useCallback, useEffect, useState } from 'react';
import { Cpu, ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from '@/components/i18n-provider';

interface AppUsage {
  agentId: string;
  username: string | null;
  name: string;
  calls: number;
  limitCalls: number;
  usedPct: number;
  sharePct: number;
  source: 'default' | 'override' | 'today';
}

interface UsagePayload {
  accountUsedPct: number;
  dailyCalls: number;
  defaultSharePct: number;
  resetsAtUtc: number;
  apps: AppUsage[];
}

function resetLabel(resetsAtUtc: number): string {
  const ms = Math.max(0, resetsAtUtc - Date.now());
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function ComputeShareCard() {
  const { t } = useTranslation();
  const [data, setData] = useState<UsagePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    fetch('/api/balance/compute-usage')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setData(j))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const raise = async (agentId: string, pct: number | null, scope: 'today' | 'always') => {
    setBusy(agentId);
    setMenuFor(null);
    try {
      await fetch('/api/balance/compute-share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId, pct, scope }),
      });
      load();
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Cpu className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">{t('wallet.computeCardTitle')}</h3>
        </div>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {t('wallet.computeCardResets', { time: resetLabel(data.resetsAtUtc) })}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground mt-0.5">
        {t('wallet.computeCardDesc', { calls: data.dailyCalls.toLocaleString(), share: data.defaultSharePct })}
      </p>

      {data.apps.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-3">{t('wallet.computeCardEmpty')}</p>
      ) : (
        <ul className="mt-3 space-y-2.5">
          {data.apps.map((app) => (
            <li key={app.agentId} className="group">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium truncate">{app.name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {app.calls.toLocaleString()} / {app.limitCalls.toLocaleString()}
                  </span>
                  <span
                    className={
                      'text-[10px] px-1.5 py-0.5 rounded-full tabular-nums ' +
                      (app.source === 'today'
                        ? 'bg-amber-500/10 text-amber-600'
                        : app.source === 'override'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground')
                    }
                    title={app.source === 'today' ? t('wallet.computeShareTodayTip') : undefined}
                  >
                    {app.sharePct}%{app.source === 'today' ? ` ${t('wallet.computeShareTodaySuffix')}` : ''}
                  </span>
                  <div className="relative">
                    <button
                      onClick={() => setMenuFor(menuFor === app.agentId ? null : app.agentId)}
                      disabled={busy === app.agentId}
                      className="h-6 px-2 text-[11px] font-medium rounded-md border border-border bg-background hover:bg-accent disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      {busy === app.agentId ? <Loader2 className="h-3 w-3 animate-spin" /> : t('wallet.computeRaise')}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    {menuFor === app.agentId && (
                      <div className="absolute right-0 top-7 z-20 w-52 rounded-md border border-border bg-popover shadow-md py-1">
                        <MenuItem label={t('wallet.computeRaiseFullToday')} onClick={() => raise(app.agentId, 100, 'today')} />
                        <MenuItem label={t('wallet.computeRaise70Today')} onClick={() => raise(app.agentId, 70, 'today')} />
                        <div className="my-1 border-t border-border" />
                        <MenuItem label={t('wallet.computeRaise70Always')} onClick={() => raise(app.agentId, 70, 'always')} />
                        <MenuItem label={t('wallet.computeRaise100Always')} onClick={() => raise(app.agentId, 100, 'always')} />
                        {app.source === 'override' && (
                          <>
                            <div className="my-1 border-t border-border" />
                            <MenuItem label={t('wallet.computeShareReset', { share: data.defaultSharePct })} onClick={() => raise(app.agentId, null, 'always')} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className={
                    'h-full rounded-full transition-all ' +
                    (app.usedPct >= 100 ? 'bg-destructive' : app.usedPct >= 80 ? 'bg-amber-500' : 'bg-primary')
                  }
                  style={{ width: `${Math.min(100, app.usedPct)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent"
    >
      {label}
    </button>
  );
}
